import BaseController from "./base"

export default class extends BaseController {
  static targets = ["form", "submitButton", "submitButtonSpinner", "submitButtonText"]

  connect() {
    // Add form submission handler
    this.formTarget.addEventListener("submit", this.validateForm.bind(this))
  }

  validateForm(event) {
    const errors = this.collectValidationErrors()

    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(errors)
      return false
    }

    // Show loading state when validation passes
    this.setSubmitButtonLoading(true)

    return true
  }

  collectValidationErrors() {
    const errors = []

    // Required fields validation with i18n field labels
    const requiredFields = [
      { name: "album[title]", labelKey: "album_title" },
      { name: "album[genre]", labelKey: "primary_genre" },
      { name: "album[original_release_date]", labelKey: "original_release_date" },
      { name: "album[release_date]", labelKey: "live_date" },
      { name: "album[label_name]", labelKey: "label_name" },
      { name: "album[p_line_year]", labelKey: "p_line_year" },
      { name: "album[p_line_copyright]", labelKey: "p_line_copyright" },
      { name: "album[c_line_year]", labelKey: "c_line_year" },
      { name: "album[c_line_copyright]", labelKey: "c_line_copyright" },
      { name: "album[upc]", labelKey: "upc" }
    ]

    requiredFields.forEach(field => {
      const input = this.formTarget.querySelector(`[name="${field.name}"]`)
      if (input && this.isFieldEmpty(input)) {
        const fieldLabel = window.I18n.t(`albums.edit.validation.fields.${field.labelKey}`)
        errors.push(window.I18n.t('albums.edit.validation.is_required', { field: fieldLabel }))
      }
    })

    // Cover image validation
    const coverImageInput = this.formTarget.querySelector('[name="album[cover_image]"]')
    const hasExistingImage = this.element.querySelector('.cover-image-preview, [data-upload-target="currentImage"]:not([style*="display: none"])')

    if (!hasExistingImage && (!coverImageInput || !coverImageInput.files || coverImageInput.files.length === 0)) {
      errors.push(window.I18n.t('albums.edit.validation.album_artwork_required'))
    }

    // Primary artist validation
    const primaryArtistInputs = this.formTarget.querySelectorAll('input[name="album[primary_artist_ids][]"]')
    if (primaryArtistInputs.length === 0) {
      errors.push(window.I18n.t('albums.edit.validation.primary_artist_required'))
    }

    // Live Date vs Original Release Date validation
    const originalReleaseDateInput = this.formTarget.querySelector('[name="album[original_release_date]"]')
    const liveDateInput = this.formTarget.querySelector('[name="album[release_date]"]')
    if (originalReleaseDateInput && liveDateInput && originalReleaseDateInput.value && liveDateInput.value) {
      const originalDate = new Date(originalReleaseDateInput.value)
      const liveDate = new Date(liveDateInput.value)
      if (liveDate < originalDate) {
        errors.push(window.I18n.t('albums.edit.validation.live_date_before_original'))
      }
    }

    return errors
  }
  
  isFieldEmpty(input) {
    if (!input) return true
    
    const value = input.value.trim()
    
    // Special handling for select elements
    if (input.tagName.toLowerCase() === 'select') {
      return !value || value === ''
    }
    
    return !value
  }
  
  showValidationErrors(errors) {
    // Remove any existing error display
    this.removeExistingErrors()

    // Create error summary
    const errorContainer = document.createElement('div')
    errorContainer.className = 'rounded-md bg-red-50 p-4 mb-6'
    errorContainer.id = 'client-validation-errors'

    const errorSummary = errors.length === 1
      ? window.I18n.t('albums.edit.validation.errors_one', { count: 1 })
      : window.I18n.t('albums.edit.validation.errors_other', { count: errors.length })

    const errorContent = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            ${errorSummary}
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
          </div>
        </div>
      </div>
    `

    errorContainer.innerHTML = errorContent
    
    // Insert error container at the top of the form
    this.formTarget.insertBefore(errorContainer, this.formTarget.firstChild)
    
    // Scroll to the top of the form to show errors
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    
    // Add red borders to empty required fields
    this.highlightErrorFields()
  }
  
  removeExistingErrors() {
    const existingErrors = document.getElementById('client-validation-errors')
    if (existingErrors) {
      existingErrors.remove()
    }

    // Remove error styling from regular fields
    this.formTarget.querySelectorAll('.border-red-500').forEach(field => {
      field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      field.classList.add('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
    })

    // Remove error styling from TomSelect fields
    this.formTarget.querySelectorAll('.ts-control.has-error').forEach(control => {
      control.classList.remove('has-error')
    })

    // Remove error styling from primary artist block
    const primaryArtistBlock = this.formTarget.querySelector('[data-artist-block="primary"]')
    if (primaryArtistBlock) {
      primaryArtistBlock.classList.remove('border-red-500')
      primaryArtistBlock.classList.add('border-gray-300')
    }
  }
  
  highlightErrorFields() {
    const requiredFields = [
      "album[title]",
      "album[genre]",
      "album[original_release_date]",
      "album[release_date]",
      "album[label_name]",
      "album[p_line_year]",
      "album[p_line_copyright]",
      "album[c_line_year]",
      "album[c_line_copyright]",
      "album[upc]"
    ]

    requiredFields.forEach(fieldName => {
      const input = this.formTarget.querySelector(`[name="${fieldName}"]`)
      if (input && this.isFieldEmpty(input)) {
        // Check if this is a TomSelect field
        if (input.tomselect) {
          // Add error class to TomSelect control
          input.tomselect.control.classList.add('has-error')
        } else {
          // Regular input field
          input.classList.remove('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
          input.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
        }
      }
    })

    // Highlight primary artist block if no primary artists
    const primaryArtistInputs = this.formTarget.querySelectorAll('input[name="album[primary_artist_ids][]"]')
    const primaryArtistBlock = this.formTarget.querySelector('[data-artist-block="primary"]')

    if (primaryArtistInputs.length === 0 && primaryArtistBlock) {
      primaryArtistBlock.classList.remove('border-gray-300')
      primaryArtistBlock.classList.add('border-red-500')
    }
  }

  setSubmitButtonLoading(loading) {
    if (!this.hasSubmitButtonTarget) return

    if (loading) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.remove('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.remove('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = window.I18n.t('albums.edit.saving')
      }
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.add('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.add('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = window.I18n.t('albums.save_unpublished_continue') + ' \u2192'
      }
    }
  }
}